import * as React from "react";
import "./index.css";
import { VersionCopyright } from "../../../generic/version";
import { MARKETING_URL, STATUS_URL, STORAGE_KEY_LOGGED_OUT_STATE } from "../../../../envVariables";
import { Alert, Button, Input, notification } from "antd";
import { CircularThemeSwitch } from "../../../CircularThemeSwitch/CircularThemeSwitch";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Globe, Lock } from "react-feather";
import { TestimonialHorizontal } from "./../testimonial-horizontal/testimonial-horizontal";
import { useAppSelector } from "../../../../store/hooks";
import { signInByProvider, signInEmail, signInSaml } from "../../../../store/userSlice";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";

const STORAGE_KEY_LOGIN_EMAIL = "LOGIN_EMAIL";
const STORAGE_KEY_LOGIN_TYPE = "LOGIN_TYPE";

type LoginType = "provider" | "email" | "saml";

const validLoginTypes: LoginType[] = ["provider", "email", "saml"];

export const SignIn: React.FC = () => {
  const theme = useAppSelector((store) => store.theme.theme);

  const { search } = useLocation();
  const navigate = useNavigate();

  const page = window.location.pathname.includes("/signup") ? "signup" : "signin";
  const showLoggedOutAlert: boolean = localStorage.getItem(STORAGE_KEY_LOGGED_OUT_STATE) === "true";

  const [loginType, setLoginType] = React.useState<LoginType>("provider");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
  const [samlProvider, setSamlProvider] = React.useState("");
  const [showSamlProviderInput, setShowSamlProviderInput] = React.useState(false);

  const loadedFromQueryRef = React.useRef<boolean>(false);
  const loadedFromStorageRef = React.useRef<boolean>(false);

  // TODO remove, only used by cypress test
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key == "q") {
        if (loginType === "saml") {
          setShowSamlProviderInput((x) => !x);
        } else {
          setLoginType((l) => (l === "email" ? "provider" : "email"));
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [loginType]);

  // Load login type and email from query params
  React.useEffect(() => {
    if (!search.startsWith("?loginType=")) {
      return;
    }

    try {
      const loginType = search.split("?loginType=")[1].split("&email=")[0];
      if (!["email", "saml"].includes(loginType)) {
        throw new Error("Saved query param is not of email or saml");
      }
      if (validLoginTypes.includes(loginType as any)) {
        setLoginType(loginType as any);
      } else {
        setLoginType("provider");
      }

      const email = search.split("?loginType=")[1].split("&email=")[1];
      setEmail(email || "");

      loadedFromQueryRef.current = true;
    } catch (e) {
      setLoginType("provider");
    }
  }, []);

  // Load saved login type and email
  React.useEffect(() => {
    if (loginType !== "saml" && loginType !== "email") {
      return;
    }

    if (!loadedFromQueryRef.current && !loadedFromStorageRef.current) {
      const localLoginType = localStorage.getItem(STORAGE_KEY_LOGIN_TYPE);
      if (localLoginType === loginType) {
        const email = localStorage.getItem(STORAGE_KEY_LOGIN_EMAIL);
        setEmail(email || "");
      }
      loadedFromStorageRef.current = true;
    }
  }, [loginType]);

  // Save login type and email
  React.useEffect(() => {
    if (!loadedFromStorageRef.current) {
      return;
    }

    if (loginType !== "saml" && loginType !== "email") {
      return;
    }

    localStorage.setItem(STORAGE_KEY_LOGIN_TYPE, loginType);

    if (!!email) {
      localStorage.setItem(STORAGE_KEY_LOGIN_EMAIL, email);
      navigate({ search: `?loginType=${loginType}&email=${email}` });
    } else {
      navigate({ search: `` });
    }
  }, [loginType, email]);

  async function tryAuthenticate(provider: "google" | "microsoft" | "github" | "email" | "saml") {
    try {
      switch (provider) {
        case "saml":
          await signInSaml(email!, samlProvider!);
          break;
        case "email":
          await signInEmail(email!, password!);
          break;
        case "google":
          await signInByProvider("google");
          break;
        case "github":
          await signInByProvider("github");
          break;
        case "microsoft":
          await signInByProvider("microsoft");
          break;
      }
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to Login", description: e.message });
      setLoginType("provider");
    }
  }

  return (
    <>
      <div
        className="signin-page flex text-normal w-full min-h-screen items-stretch p-16 flex-col"
        style={{ backgroundColor: "var(--color-drop)" }}
      >
        <div className="w-full flex justify-center mb-4">
          <a href={`${MARKETING_URL}`} target={"_blank"}>
            <img
              className="controlplane-logo"
              height={"auto"}
              src={`/resources/logos/controlPlaneLogo${theme === "dark" ? "White" : ""}.svg`}
              alt="Control Plane Logo"
            />
          </a>
        </div>{" "}
        {showLoggedOutAlert && (
          <Alert
            showIcon
            message="You have been automatically logged out of the application due to inactivity. You can configure session timeout from Org info page."
            type="warning"
            className="w-fit mx-auto my-2"
          />
        )}
        {page === "signin" ? (
          <div className="w-full flex justify-center mb-12 mt-2">
            <div className="text-4xl">Sign In to Control Plane</div>
          </div>
        ) : (
          <>
            <div className="w-full flex justify-center mb-4 mt-2">
              <div className="text-4xl">Create a Control Plane Account</div>
            </div>
            <div className="w-full flex justify-center mb-12 mt-2">
              <div className="text-2xl">Access The Enterprise Platform to Run Unbreakable Cloud-Native Apps</div>
            </div>
          </>
        )}
        {loginType !== "provider" ? (
          <div className="flex flex-col items-center w-full" data-hs-do-not-collect="true">
            <div style={{ width: 300 }} className="flex flex-col">
              <div>
                <label htmlFor="email">Email</label>
              </div>
              <Input id={`email`} style={{ width: 300 }} value={email} onChange={(e) => setEmail(e.target.value)} />
              {loginType === "email" ? (
                <>
                  <div>
                    <label htmlFor="password">Password</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      id={`password`}
                      style={{ width: 300 }}
                      type={isPasswordVisible ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <Button
                      icon={isPasswordVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                      onClick={() => setIsPasswordVisible((x) => !x)}
                    />
                  </div>
                </>
              ) : null}
              {loginType === "saml" && showSamlProviderInput ? (
                <>
                  <div>SAML Provider</div>
                  <Input
                    style={{ width: 300 }}
                    value={samlProvider}
                    onChange={(e) => setSamlProvider(e.target.value)}
                  />
                </>
              ) : null}
              <Button
                className="mt-4"
                style={{ width: 300 }}
                onClick={() => {
                  if (loginType === "email") {
                    tryAuthenticate("email");
                  } else {
                    tryAuthenticate("saml");
                  }
                }}
                type={"primary"}
              >
                Log In
              </Button>
              <Button style={{ width: 300 }} className="my-2" onClick={() => setLoginType("provider")} type={"default"}>
                Back
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center w-full text-sm font-medium" data-hs-do-not-collect="true">
            <button
              type={"button"}
              onClick={() => tryAuthenticate("google")}
              className={`mb-4 flex items-center pl-4`}
              style={{
                width: 220,
                height: 40,
                backgroundColor: "var(--color-white)",
                boxShadow: `0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%)`,
              }}
            >
              <span>
                <img width={18} height={18} src="/resources/logos/login/google.svg" />
              </span>
              <span className="pl-4 font-medium" style={{ color: "#757575" }}>
                Continue with Google
              </span>
            </button>
            <button
              type={"button"}
              onClick={() => tryAuthenticate("github")}
              className={`mb-4 flex items-center pl-4`} //
              style={{
                width: 220,
                height: 40,
                boxShadow: `0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%)`,
                backgroundColor: "#333",
                color: "white",
              }}
            >
              <span>
                <img width={18} height={18} src="/resources/logos/login/github.svg" />
              </span>
              <span className="pl-4 font-medium">Continue with Github</span>
            </button>
            <button
              type={"button"}
              onClick={() => tryAuthenticate("microsoft")}
              className={`mb-4 flex items-center pl-4`} //
              style={{
                width: 220,
                height: 40,
                boxShadow: `0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%)`,
                backgroundColor: "#2f2f2f",
                color: "white",
              }}
            >
              <span>
                <img width={18} height={18} src="/resources/logos/login/microsoft.svg" />
              </span>
              <span className="pl-4">Continue with Microsoft</span>
            </button>
            <button
              type={"button"}
              onClick={() => setLoginType("saml")}
              className={`mb-4 flex items-center pl-4`} //
              style={{
                width: 220,
                height: 40,
                boxShadow: `0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%)`,
                backgroundColor: "#2f2f2f",
                color: "white",
              }}
            >
              <span style={{ width: 18, height: 18 }} className="relative">
                <span className="absolute inset-0">
                  <Globe style={{ width: "100%", height: "100%" }} />
                </span>
                <span className="absolute" style={{ top: -4, right: -6 }}>
                  <Lock style={{ width: 10, height: 10 }} />
                </span>
              </span>
              <span className="pl-4">Continue with SAML</span>
            </button>
          </div>
        )}
        {page === "signin" ? (
          <div className="flex items-center w-full justify-center gap-2">
            <div>New to Control Plane?</div>
            <Link to={"/signup"} style={{ color: "var(--color-blue-300)" }}>
              Sign Up
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center w-full justify-center gap-2">
              <div>Already have an account?</div>
              <Link to={"/signin"} style={{ color: "var(--color-blue-300)" }}>
                Sign In
              </Link>
            </div>
            <TestimonialHorizontal
              quote="With Control Plane, we can go from concept to production so much faster. What used to take weeks or months now takes an hour."
              company="CEO at Verdova"
              photo={"/resources/images/maxFeinberg-verdova.jpeg"}
              name={"Max Feinberg"}
            />
          </>
        )}
        <div className="flex-grow" />
        <div className="mt-16">
          <div className={"flex items-center justify-center mb-2"}>
            <CircularThemeSwitch />
          </div>
          <div className="flex items-center justify-center">
            <a
              className="color-link font-semibold"
              href={`${STATUS_URL}`}
              target="_blank"
              rel="noopener"
            >{`System Status Page`}</a>
          </div>
          <div className="text-footer text-center">
            <VersionCopyright /> {new Date().getFullYear()} Control Plane. All rights reserved.
          </div>
        </div>
      </div>
    </>
  );
};
